import { FC } from 'react';
import { Alert, Stack } from '@mui/material';
import { InputSelect, InputText } from '@farmshare/ui-components';
import { getIn, useFormikContext } from 'formik';

const PRICING_MODEL_OPTIONS = [
  { value: '', label: 'No Additional Charge' },
  { value: 'per_pound', label: 'Per Pound ($/lb)' },
  { value: 'per_item', label: 'Per Item' },
  { value: 'per_hanging_weight', label: 'Per Hanging Weight ($/lb HW)' },
  { value: 'fixed_price', label: 'Fixed Price' },
];

interface PricingFieldsProps {
  /**
   * Formik dot-notation prefix for the pricing fields.
   * Defaults to 'pricing', which maps to `pricing.model`, `pricing.price`, etc.
   * Pass a different prefix to render pricing at a nested path, e.g.
   * 'options[0].pricing' for per-option pricing.
   */
  namePrefix?: string;
  /** Input size — passed through to the underlying inputs. */
  size?: 'small' | 'medium';
}

/**
 * Reusable pricing sub-form. Expects the parent Formik context to have a
 * pricing object at `namePrefix`: `{ model, price, minimumCharge? }`.
 * Price and minimum charge fields are only shown when a model is selected.
 * @param props - Component props
 * @param props.namePrefix - Formik path prefix (default `'pricing'`)
 * @param props.size - Input size (default `'medium'`)
 * @returns Pricing fields component
 */
export const PricingFields: FC<PricingFieldsProps> = ({
  namePrefix = 'pricing',
  size = 'medium',
}) => {
  // Use getIn so this works at any nested Formik path.
  const { values } = useFormikContext();
  const hasModel = Boolean(getIn(values, `${namePrefix}.model`));

  return (
    <Stack spacing={2}>
      <InputSelect
        label="Pricing Model"
        nameOverride={`${namePrefix}.model`}
        options={PRICING_MODEL_OPTIONS}
        size={size}
      />

      {/* Only show price/charge fields when a billing model is selected */}
      {hasModel ? (
        <>
          <InputText
            label="Price"
            nameOverride={`${namePrefix}.price`}
            type="number"
            size={size}
          />
          <InputText
            label="Minimum Charge (optional)"
            nameOverride={`${namePrefix}.minimumCharge`}
            type="number"
            size={size}
          />
        </>
      ) : (
        <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
          No pricing will be applied to this item.
        </Alert>
      )}
    </Stack>
  );
};
